/* Multi-floor projects (DOM-free, unit-tested). A project is a family of
   layouts named "<project> · <floor>" — no new storage, no drift on rename:
   the name IS the membership. */
export const SEP = ' · ';

export function projectOf(name: string | null): { project: string; floor: string } | null {
  if (!name) return null;
  const i = name.lastIndexOf(SEP);
  if (i <= 0 || i + SEP.length >= name.length) return null;
  return { project: name.slice(0, i), floor: name.slice(i + SEP.length) };
}

const natural = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
/** Rank: basements first, then ground, then numbered/other floors in natural order. */
const rank = (floor: string) => /basement|cellar|lower/i.test(floor) ? 0 : /ground|main|first/i.test(floor) ? 1 : 2;

/** The project's floors (full layout names), bottom to top. */
export function floorsOf(names: string[], project: string): string[] {
  return names
    .filter(n => projectOf(n)?.project === project)
    .sort((a, b) => {
      const fa = projectOf(a)!.floor, fb = projectOf(b)!.floor;
      return rank(fa) - rank(fb) || natural.compare(fa, fb);
    });
}

export const floorName = (project: string, floor: string) => `${project}${SEP}${floor.trim()}`;

/** A default label for the next floor up: "Floor 2", "Floor 3", … (skipping taken ones). */
export function nextFloorLabel(floorNames: string[]): string {
  const taken = new Set(floorNames.map(n => projectOf(n)?.floor.toLowerCase()));
  for (let i = 2; ; i++) if (!taken.has(`floor ${i}`)) return `Floor ${i}`;
}

/** The floor directly under `current` in the project, if any. */
export function floorBelow(floors: string[], current: string): string | null {
  const i = floors.indexOf(current);
  return i > 0 ? floors[i - 1] : null;
}
